import { createContext, useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { v4 as uuid } from "uuid"
import partition from "lodash.partition"

import { isNonEmptyArray } from "web/services/helpers"
import useResizeObserver from "web/hooks/behaviour/useResizeObserver"
import { useCurrentUser } from "web/hooks/data"

import TitleRow from "./TitleRow"
import GroupByIndicator from "./GroupByIndicator"
import RowGroups from "./RowGroups"
import DataRows from "./DataRows"
import TableSummary from "./TableSummary"

import {
  getCells,
  getColumnsToDisplay,
  getGridTemplateColumns,
} from "./helpers"
import useVisibleColumnCount from "./useVisibleColumnCount"

export const DataTableContext = createContext()
DataTableContext.displayName = "DataTableContext"

export default function DataTable({
  columns,
  data: unsortedData,
  groupCollection,
  groupTotals,
  grandTotals,
  stickyGroupTotals,
  stickyGrandTotals,
  pageNumber = null,
  sortColumn = null,
  sortDesc = false,
  groupColumn = null,
  groupDesc = false,
  secondGroupColumn = null,
  secondGroupDesc = false,
  collapsedGroups = [],
  enableFrozenColumns,
  itemLabel = "Row",
  onSort,
  onGroup,
  onGroupCollapseStateChanged,
  onRowClick,
  onRowSelectionChanged,
  onSelectAllChanged,
  onFrozenFieldChanged,
  onMoveColumn,
  onResizeColumn,
  renderToEditCell,
  isEditable = false,
  isCompact = false,
  showEmptyTable = false,
  showGroupTotals = false,
  showGrandTotals = false,
  showDetails: showDetailsProp = true,
  openLockedFieldEditModal = null,
  isModalEditable = null,
  openEditModal = null,
  showRowNumbers = false,
}) {
  const { current: tableId } = useRef(uuid())
  const headerRef = useRef()
  const tableBodyContainerRef = useRef()
  const tableRef = useRef()
  const [headerHeight, setHeaderHeight] = useState(0)

  const hasData = isNonEmptyArray(unsortedData)

  const isDataGrouped = groupColumn !== null && groupCollection.length > 0
  const hasSecondGroup = isDataGrouped && secondGroupColumn !== null

  const groupColumnLabel =
    isDataGrouped && columns.find((el) => el.dataKey === groupColumn)?.label

  const secondGroupColumnLabel =
    hasSecondGroup &&
    columns.find((el) => el.dataKey === secondGroupColumn)?.label

  const canHideDetails = isDataGrouped && showGroupTotals
  const showDetails = canHideDetails ? showDetailsProp : true
  const showDataRows = !isDataGrouped && showDetails

  const canSelectRow =
    typeof onRowSelectionChanged === "function" && showDetails
  const canMoveColumns = typeof onMoveColumn === "function"
  const canResizeColumns = typeof onResizeColumn === "function"
  const canGroupTable = typeof onGroup === "function"
  const canCollapseGroups = typeof onGroupCollapseStateChanged === "function"

  const newDesc = (curCol, column, curDesc) =>
    curCol === null || curCol !== column || curDesc === true ? false : true

  useEffect(() => {
    const currentRef = tableBodyContainerRef.current
    if (currentRef) {
      currentRef.scrollTop = 0
    }
  }, [pageNumber])

  const visibleColumnCount = useVisibleColumnCount(columns, tableRef)

  const { frozenColumnCount, columnsToDisplay } = getColumnsToDisplay({
    columns,
    visibleColumnCount,
    groupColumn,
    secondGroupColumn,
    hasSecondGroup,
    hidingDetails: !showDetails,
    canSelectRow,
    showRowNumbers,
  })

  const columnCount =
    columnsToDisplay.length + (canSelectRow ? 1 : 0) + (showRowNumbers ? 1 : 0)

  useEffect(() => {
    if (!isEditable) return

    const isTabable = (cell) => cell.tabIndex !== undefined
    const isVisible = (cell) => cell.ref.current !== null
    const isSelectable = (cell) => isTabable(cell) && isVisible(cell)

    const firstSelectableCell = gridCells.flat().find(isSelectable)
    if (!firstSelectableCell) return

    firstSelectableCell.ref.current.focus()

    // we only want to do this when isEditable has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable])

  const rowGroups = useMemo(() => {
    if (showDetails) {
      return groupCollection?.map((group) => {
        const data = unsortedData.filter((row) =>
          group.subject_ids.includes(row.id)
        )

        const [stickyRows, otherRows] = partition(data, (row) => row.sticky)
        return {
          groupKey: group.key,
          groupSize: group.size,
          groupValues: group.group_values,
          data: stickyRows.concat(otherRows),
          expanded: collapsedGroups && !collapsedGroups.includes(group.key),
        }
      })
    } else {
      return groupTotals.map((group) => {
        return {
          groupKey: group.key,
          groupSize: group.size,
          groupValues: group.group_values,
          data: [{ values: group.field_values }],
        }
      })
    }
  }, [groupCollection, groupTotals, collapsedGroups, showDetails, unsortedData])

  const [stickyRows, otherRows] = partition(unsortedData, (row) => row.sticky)
  const sortedData = stickyRows.concat(otherRows)

  const data = sortedData

  const hasMultipleStickyRows = data.filter((row) => row.sticky).length > 1

  const setHeaderHeightCallback = ([entry]) => {
    if (entry?.contentRect) {
      setHeaderHeight(entry.contentRect.height)
    }
  }

  useResizeObserver(headerRef, setHeaderHeightCallback)

  const gridTemplateColumns = useMemo(
    () =>
      getGridTemplateColumns({
        columns: columnsToDisplay,
        canSelectRow,
        showRowNumbers,
      }),
    [columnsToDisplay, canSelectRow, showRowNumbers]
  )

  const currentUser = useCurrentUser()
  const currentUserIsDataAdmin = currentUser.is_data_admin

  const gridCells = useMemo(
    () =>
      hasData &&
      getCells({
        rows: data,
        columns: columnsToDisplay,
        tableIsEditable: isEditable,
        currentUserIsDataAdmin: currentUserIsDataAdmin,
      }),
    [columnsToDisplay, hasData, data, isEditable, currentUserIsDataAdmin]
  )

  if (!hasData && !showEmptyTable) return null

  function handleSort(column, sortDirection) {
    if (!onSort) return

    if (sortDirection) {
      onSort(column, sortDirection === "desc")
    } else {
      onSort(column, newDesc(sortColumn, column, sortDesc))
    }
  }

  async function handleGroup(column) {
    onGroup && (await onGroup(column))

    if (column === null) {
      onGroupCollapseStateChanged([])
    }
  }

  function handleRowSelectionChanged(rowData) {
    if (!canSelectRow) return

    onRowSelectionChanged({
      ...rowData,
      selected: !rowData.selected,
    })
  }

  const allRowsSelected = unsortedData.every((el) => el.selected)

  function handleSelectAllChanged() {
    onSelectAllChanged(!allRowsSelected)
  }

  function handleMoveColumn(fromColumn, toColumn) {
    if (!canMoveColumns) return

    onMoveColumn(fromColumn, toColumn)
  }

  function handleResizeColumn(column, newWidth) {
    if (!canResizeColumns) return

    onResizeColumn(column, newWidth)
  }

  const tableContext = {
    data,
    groupTotals,
    stickyGroupTotals,
    columns: columnsToDisplay,
    cells: gridCells,
    columnCount,
    gridTemplateColumns,
    canSelectRow,
    canMoveColumns,
    canGroupTable,
    canCollapseGroups,
    sortColumn,
    sortDesc,
    groupColumn,
    groupDesc,
    groupColumnLabel,
    secondGroupColumn,
    secondGroupDesc,
    secondGroupColumnLabel,
    itemLabel,
    renderToEditCell,
    onRowClick,
    onSort: handleSort,
    onGroup: handleGroup,
    onMoveColumn: handleMoveColumn,
    onResizeColumn: handleResizeColumn,
    onRowSelectionChanged: handleRowSelectionChanged,
    onSelectAllChanged: handleSelectAllChanged,
    onFrozenFieldChanged,
    allRowsSelected,
    isEditable,
    isCompact,
    hasFrozenColumns: frozenColumnCount !== 0,
    enableFrozenColumns,
    showGroupTotals,
    showGrandTotals,
    showDetails,
    openLockedFieldEditModal,
    isModalEditable,
    openEditModal,
    showRowNumbers,
  }

  const tableClasses = classNames(
    "flex flex-col min-h-0 grow box-border overflow-x-hidden overflow-y-hidden",
    {
      "pb-2.5": !isCompact,
    }
  )

  const dataTableHeaderAndBodyClasses = classNames(
    "min-w-full relative grow overflow-x-auto overflow-y-auto",
    {
      "px-0": isCompact,
      "min-h-[190px] -mr-5 pr-5": !isCompact,
      grouped: isDataGrouped,
    }
  )

  const tableBodyContainer = classNames(
    "box-border inline min-w-full overscroll-y-contain rounded-b border-neutral-9"
  )

  return (
    <div id={tableId} ref={tableRef} className={tableClasses}>
      <div className={dataTableHeaderAndBodyClasses}>
        <DataTableContext.Provider value={tableContext}>
          <TitleRow ref={headerRef} />
          <div className={tableBodyContainer} ref={tableBodyContainerRef}>
            <div
              className={classNames(
                "box-border inline-grid min-w-full rounded-b bg-white text-sm",
                {
                  "columns-moveable": canMoveColumns,
                }
              )}
              style={{ gridTemplateColumns }}
            >
              {isDataGrouped && !isCompact && (
                <GroupByIndicator
                  groupColumnLabel={groupColumnLabel}
                  groupColumn={groupColumn}
                  groupDesc={groupDesc}
                  secondGroupColumnLabel={secondGroupColumnLabel}
                  secondGroupColumn={secondGroupColumn}
                  secondGroupDesc={secondGroupDesc}
                  onGroup={onGroup}
                  showDetails={showDetails}
                  columnCount={columnCount}
                  hasFrozenColumns={!!frozenColumnCount}
                  headerHeight={headerHeight}
                />
              )}
              {isDataGrouped && (
                <RowGroups
                  rowGroups={rowGroups}
                  collapsedGroups={collapsedGroups}
                  onGroupCollapseStateChanged={onGroupCollapseStateChanged}
                  onRowClick={onRowClick}
                />
              )}
              {showDataRows && <DataRows rows={data} onRowClick={onRowClick} />}
              {showGrandTotals && (
                <>
                  {hasMultipleStickyRows && (
                    <TableSummary
                      grandTotals={stickyGrandTotals}
                      isStickySummary
                    />
                  )}
                  <TableSummary grandTotals={grandTotals} />
                </>
              )}
            </div>
          </div>
        </DataTableContext.Provider>
      </div>
    </div>
  )
}
